/**
 * Seção "Medidas de controle" da janela de dados do risco (#606, E2): o que já existe hoje, em
 * dois cartões com as caixas em duas colunas; os EPIs em uso como cartões, com C.A., atenuação e
 * eficácia; e as medidas propostas, recolhidas porque são opcionais.
 */

import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Controller, useFormContext } from 'react-hook-form';
import {
  EXISTING_CONTROL_MEASURES,
  type ExistingControlMeasureCategory,
} from '../../../types/controlMeasures';
import type { FormularioDoRisco } from '../RiskApplicationModal';
import { ALVO } from '../completudeDoRisco';
import { RotuloQueConta } from './comum';

export interface OpcaoDeEpi {
  id: number;
  nome: string;
  /** O número do C.A. — o cadastro de EPI o guarda em `metodo`. */
  metodo: string;
  atenuacao?: string | null;
  /** EFICAZ, NAO_EFICAZ ou nula quando ninguém informou no cadastro do EPI. */
  efficiency?: string | null;
  /** O EPI foi marcado antes, mas não está mais ligado ao risco no catálogo. */
  orphaned?: boolean;
}

const CAMPO_DA_LISTA: Record<ExistingControlMeasureCategory, 'existingAdministrativeMeasures' | 'existingEngineeringMeasures'> = {
  ADMINISTRATIVE: 'existingAdministrativeMeasures',
  ENGINEERING: 'existingEngineeringMeasures',
};

const CAMPO_DO_OUTROS: Record<ExistingControlMeasureCategory, 'existingAdministrativeOther' | 'existingEngineeringOther'> = {
  ADMINISTRATIVE: 'existingAdministrativeOther',
  ENGINEERING: 'existingEngineeringOther',
};

const TITULO_DO_CARTAO: Record<ExistingControlMeasureCategory, string> = {
  ADMINISTRATIVE: 'Administrativas',
  ENGINEERING: 'Engenharia / EPC',
};

const CartaoDeMedidas: React.FC<{ categoria: ExistingControlMeasureCategory; desabilitada: boolean }> = ({
  categoria,
  desabilitada,
}) => {
  const { control } = useFormContext<FormularioDoRisco>();
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 0 }}>
      <Typography variant="overline" color="text.secondary">
        {TITULO_DO_CARTAO[categoria]}
      </Typography>
      <Controller
        name={CAMPO_DA_LISTA[categoria]}
        control={control}
        render={({ field }) => (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1 }}>
            {EXISTING_CONTROL_MEASURES[categoria].map((medida) => (
              <FormControlLabel
                key={medida}
                label={<Typography variant="body2">{medida}</Typography>}
                control={
                  <Checkbox
                    checked={field.value.includes(medida)}
                    disabled={desabilitada}
                    onChange={() =>
                      field.onChange(
                        field.value.includes(medida) ? field.value.filter((m) => m !== medida) : [...field.value, medida],
                      )
                    }
                  />
                }
              />
            ))}
          </Box>
        )}
      />
      <Controller
        name={CAMPO_DO_OUTROS[categoria]}
        control={control}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Outros"
            size="small"
            fullWidth
            disabled={desabilitada}
            sx={{ mt: 1 }}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
          />
        )}
      />
    </Paper>
  );
};

const TEXTO_DA_EFICACIA: Record<string, string> = { EFICAZ: 'Eficaz', NAO_EFICAZ: 'Não eficaz' };

const CartaoDeEpi: React.FC<{ opcao: OpcaoDeEpi; marcado: boolean; desabilitado: boolean; aoAlternar: () => void }> = ({
  opcao,
  marcado,
  desabilitado,
  aoAlternar,
}) => {
  const semCa = !opcao.metodo?.trim();
  return (
    <Paper
      variant="outlined"
      data-testid={`epi-${opcao.id}`}
      sx={{ p: 1.5, borderColor: marcado ? 'primary.main' : 'divider', borderWidth: marcado ? 2 : 1 }}
    >
      <FormControlLabel
        sx={{ alignItems: 'flex-start', m: 0 }}
        control={<Checkbox checked={marcado} disabled={desabilitado} onChange={aoAlternar} sx={{ mt: -0.5 }} />}
        label={
          <Box>
            <Typography variant="body2" fontWeight={600} color={opcao.orphaned ? 'warning.dark' : 'text.primary'}>
              {opcao.nome}
            </Typography>
            {opcao.orphaned && (
              <Typography variant="caption" color="warning.dark" display="block">
                Não está mais ligado a este risco
              </Typography>
            )}
            {semCa ? (
              <Typography variant="body2" color="error.dark" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <WarningAmberIcon fontSize="small" />
                Sem C.A. — o eSocial exige
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                C.A. {opcao.metodo}
              </Typography>
            )}
            {opcao.atenuacao && (
              <Typography variant="body2" color="text.secondary">
                Atenuação {opcao.atenuacao}
              </Typography>
            )}
            <Typography variant="body2" color={opcao.efficiency ? 'text.secondary' : 'error.dark'}>
              {opcao.efficiency ? TEXTO_DA_EFICACIA[opcao.efficiency] ?? opcao.efficiency : 'Eficaz? Não informado'}
            </Typography>
          </Box>
        }
      />
    </Paper>
  );
};

interface SecaoMedidasDeControleProps {
  opcoesDeEpi: OpcaoDeEpi[];
  desabilitada: boolean;
  /** O risco da aplicação: leva à ficha dele, onde o EPI se liga ao risco e se corrige C.A. e eficácia. */
  riskId?: number;
}

export const SecaoMedidasDeControle: React.FC<SecaoMedidasDeControleProps> = ({ opcoesDeEpi, desabilitada, riskId }) => {
  const { control, watch } = useFormContext<FormularioDoRisco>();
  const temProposta = Boolean(watch('proposedAdministrative') || watch('proposedEngineering') || watch('proposedEpi'));
  const [propostasAbertas, setPropostasAbertas] = React.useState(temProposta);

  return (
    <Stack spacing={3}>
      <Box id={ALVO.medidas} tabIndex={-1}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          <RotuloQueConta texto="Já existem hoje" />
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <CartaoDeMedidas categoria="ADMINISTRATIVE" desabilitada={desabilitada} />
          <CartaoDeMedidas categoria="ENGINEERING" desabilitada={desabilitada} />
        </Stack>
      </Box>

      <Box id={ALVO.epis} tabIndex={-1}>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          <RotuloQueConta texto="EPIs em uso" conta={opcoesDeEpi.length > 0} />
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Só aparecem aqui os EPIs ligados a este risco no catálogo; C.A. e eficácia vêm do cadastro do EPI.{' '}
          {riskId != null && (
            <Link href={`/occupational-risks/${riskId}`} target="_blank" rel="noopener">
              Abrir a ficha do risco
            </Link>
          )}
        </Typography>
        {opcoesDeEpi.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Nenhum EPI ligado a este risco.
          </Typography>
        ) : (
          <Controller
            name="existingEpiIds"
            control={control}
            render={({ field }) => (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 1.5 }}>
                {opcoesDeEpi.map((opcao) => (
                  <CartaoDeEpi
                    key={opcao.id}
                    opcao={opcao}
                    marcado={field.value.includes(opcao.id)}
                    desabilitado={desabilitada}
                    aoAlternar={() =>
                      field.onChange(
                        field.value.includes(opcao.id)
                          ? field.value.filter((id) => id !== opcao.id)
                          : [...field.value, opcao.id],
                      )
                    }
                  />
                ))}
              </Box>
            )}
          />
        )}
      </Box>

      <Box>
        <Button
          type="button"
          variant="text"
          onClick={() => setPropostasAbertas((aberta) => !aberta)}
          aria-expanded={propostasAbertas}
          sx={{ textTransform: 'none', px: 0 }}
        >
          {propostasAbertas ? 'Esconder medidas propostas' : 'Adicionar medidas propostas (opcional)'}
        </Button>
        <Collapse in={propostasAbertas}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {(
              [
                ['proposedAdministrative', 'Administrativas propostas'],
                ['proposedEngineering', 'Engenharia / EPC propostas'],
                ['proposedEpi', 'EPIs propostos'],
              ] as const
            ).map(([nome, rotulo]) => (
              <Controller
                key={nome}
                name={nome}
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={rotulo}
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={desabilitada}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            ))}
          </Stack>
        </Collapse>
      </Box>
    </Stack>
  );
};

export default SecaoMedidasDeControle;
